// Beobachter-Feld einer Aufgabe (#854) — WER beobachtet, plus Picker zum
// Ein-/Austragen anderer Personen.
//
// Beobachter erhalten die Status- und Kommentar-Benachrichtigungen der Aufgabe;
// der Merge in die Empfängerliste existierte schon (lib/taskSubscriptions.ts),
// bisher konnte man aber nur SICH SELBST eintragen (der „Beobachten"-Knopf,
// WatchToggle) und sah nur eine Anzahl.
//
// Bedienung absichtlich identisch zum Zuständigen-Feld (AssigneePicker,
// Mehrfachauswahl): eine Auswahl ist die gewünschte Endliste, die Differenz
// wird als einzelne Abos geschrieben — POST /watchers pro neuer Person,
// DELETE /watchers/:userId pro entfernter. Kein Set-Replace-Endpunkt, damit ein
// paralleler Selbst-Abo-Klick nicht stillschweigend überschrieben wird.
//
// Kandidaten sind bewusst NICHT auf Projekt-Mitglieder begrenzt: Zugriff kommt
// in juuplan meistens über Space-/Abteilungs-Vererbung, `project_members` ist
// in vielen Projekten leer — eine Mitglieder-Begrenzung zeigte dort fast
// niemanden. Wer keinen Zugriff hat, wird von der Route mit einem 400 samt
// Namen abgelehnt (api() toastet die Meldung).

import { useEffect, useState } from "preact/hooks";
import { api } from "../lib/api";
import { AssigneePicker } from "./AssigneePicker";
import { AvatarStack } from "./AvatarStack";

export interface TaskWatchState {
  watching: boolean;
  count: number;
  /** Alle Beobachter. Fehlt bei einem Server vor #854. */
  user_ids?: number[];
}

interface Props {
  taskId: number;
  /** Nur-Lese-Ansicht (Viewer/Gast): Avatare ohne Picker. */
  readOnly?: boolean;
  /** Meldet einen neuen Zustand nach oben, damit der „Beobachten"-Knopf mitläuft. */
  onChanged?: (state: TaskWatchState) => void;
}

export function TaskWatchers(props: Props): preact.JSX.Element | null {
  const { taskId, readOnly = false, onChanged } = props;
  const [ids, setIds] = useState<number[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setIds(null);
    api<TaskWatchState>(`/api/tasks/${taskId}/watch`, { silentError: true })
      .then((s) => {
        if (alive) setIds(s.user_ids ?? []);
      })
      .catch(() => {
        /* still — das Feld bleibt ausgeblendet, wie beim WatchToggle */
      });
    return () => {
      alive = false;
    };
  }, [taskId]);

  if (ids === null) return null;

  const apply = async (next: number[]): Promise<void> => {
    if (busy) return;
    const added = next.filter((id) => !ids.includes(id));
    const removed = ids.filter((id) => !next.includes(id));
    if (added.length === 0 && removed.length === 0) return;

    setBusy(true);
    // Optimistisch, aber die Server-Antwort gewinnt: eine abgelehnte Person
    // (kein Projektzugriff) muss aus der Anzeige wieder verschwinden.
    setIds(next);
    let last: TaskWatchState | null = null;
    try {
      for (const id of added) {
        last = await api<TaskWatchState>(`/api/tasks/${taskId}/watchers`, {
          method: "POST",
          body: { user_id: id },
        });
      }
      for (const id of removed) {
        last = await api<TaskWatchState>(`/api/tasks/${taskId}/watchers/${id}`, {
          method: "DELETE",
        });
      }
    } catch {
      // api() hat die Server-Meldung schon getoastet.
    } finally {
      // Immer neu lesen: nach einem Teilfehler ist der lokale Stand geraten.
      try {
        const fresh = await api<TaskWatchState>(`/api/tasks/${taskId}/watch`, {
          silentError: true,
        });
        setIds(fresh.user_ids ?? []);
        onChanged?.(fresh);
      } catch {
        if (last) {
          setIds(last.user_ids ?? []);
          onChanged?.(last);
        }
      }
      setBusy(false);
    }
  };

  if (readOnly) {
    return ids.length > 0 ? (
      <AvatarStack userIds={ids} size="md" />
    ) : (
      <span class="text-xs text-[color:var(--color-on-surface-variant)]">
        — Niemand —
      </span>
    );
  }

  return (
    <AssigneePicker
      value={ids}
      onChange={apply}
      label="Beobachter"
      emptyLabel="— Niemand —"
    />
  );
}
